import { useState, useRef } from 'react'
import toast from 'react-hot-toast'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import {
  parseCSVContent,
  generateCSVTemplate,
  type CSVParseResult,
} from '../../utils/csvParser'
import { ImportCSVPreviewTable } from './ImportCSVPreviewTable'
import { deliveriesAPI } from '../../api/deliveries'

interface ImportCSVModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: (importedCount: number) => void
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'done'

const OVERLAY: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.65)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 2000,
  backdropFilter: 'blur(4px)',
}

const MODAL: React.CSSProperties = {
  background: '#1A1C1E',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: 20,
  width: '100%',
  maxWidth: 700,
  maxHeight: '90vh',
  overflowY: 'auto',
  padding: '28px 32px',
  fontFamily: 'DM Sans, sans-serif',
  color: '#fff',
}

const StatBadge = ({ label, value, color }: { label: string; value: number; color: string }) => (
  <div style={{
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    padding: '10px 20px',
    textAlign: 'center',
    flex: 1,
  }}>
    <div style={{ color, fontSize: 22, fontWeight: 700 }}>{value}</div>
    <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, marginTop: 2 }}>{label}</div>
  </div>
)

export const ImportCSVModal = ({ isOpen, onClose, onSuccess }: ImportCSVModalProps) => {
  const queryClient = useQueryClient()
  const [step, setStep] = useState<ImportStep>('upload')
  const [parseResult, setParseResult] = useState<CSVParseResult | null>(null)
  const [progress, setProgress] = useState({ current: 0, total: 0 })
  const [results, setResults] = useState({ success: 0, failed: 0 })
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({})
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  if (!isOpen) return null

  const handleDownloadTemplate = () => {
    const content = generateCSVTemplate()
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'tariqgo-import-template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const readFile = (file: File) => {
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      toast.error('Seuls les fichiers .csv sont acceptés')
      return
    }
    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target?.result as string
      if (!content) return
      const result = parseCSVContent(content)
      if (result.totalCount > 200) {
        toast.error(`Maximum 200 colis par import. Votre fichier en contient ${result.totalCount}`)
        return
      }
      if (result.totalCount === 0) {
        toast.error('Le fichier CSV est vide ou ne contient que le header.')
        return
      }
      setParseResult(result)
      setStep('preview')
    }
    reader.readAsText(file, 'UTF-8')
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) readFile(file)
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) readFile(file)
  }

  const handleImport = async () => {
    if (!parseResult) return
    const validRows = parseResult.rows.filter(r => r.valid && r.parsed)
    setProgress({ current: 0, total: validRows.length })
    setRowErrors({})
    setStep('importing')

    let success = 0
    let failed = 0
    const errors: Record<number, string> = {}

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i]
      const parsed = row.parsed!
      try {
        await deliveriesAPI.create({
          dropoff: {
            address:      parsed.dropoffAddress,
            contactName:  parsed.contactName,
            contactPhone: parsed.contactPhone,
            notes:        parsed.notes,
          },
          package: {
            size:    parsed.size,
            weight:  parsed.weight,
            fragile: parsed.fragile,
          },
          price: { base: parsed.price },
          paymentMethod: parsed.paymentMethod,
        })
        success++
      } catch (err: unknown) {
        failed++
        const message = err instanceof AxiosError
          ? (err.response?.data?.message ?? err.response?.data?.error ?? 'Erreur API')
          : 'Erreur inconnue'
        errors[row.index] = message
      }
      setProgress({ current: i + 1, total: validRows.length })
    }

    setResults({ success, failed })
    setRowErrors(errors)
    queryClient.invalidateQueries({ queryKey: ['deliveries'] })
    setStep('done')
  }

  const handleClose = () => {
    setStep('upload')
    setParseResult(null)
    setRowErrors({})
    onClose()
  }

  const percent = progress.total > 0
    ? Math.round((progress.current / progress.total) * 100)
    : 0

  return (
    <div style={OVERLAY} onClick={(e) => { if (e.target === e.currentTarget) handleClose() }}>
      <div style={MODAL}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Import CSV</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>
              Importez plusieurs colis en masse depuis un fichier CSV
            </p>
          </div>
          {step !== 'importing' && (
            <button
              onClick={handleClose}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 22, lineHeight: 1 }}
            >
              ×
            </button>
          )}
        </div>

        {/* Step: upload */}
        {step === 'upload' && (
          <div>
            <button
              onClick={handleDownloadTemplate}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'rgba(29,158,117,0.12)',
                border: '1px solid rgba(29,158,117,0.3)',
                borderRadius: 8, color: '#1D9E75',
                fontSize: 13, fontWeight: 600,
                padding: '8px 14px', cursor: 'pointer',
                fontFamily: 'inherit', marginBottom: 20,
              }}
            >
              ↓ Télécharger le modèle CSV
            </button>

            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? '#1D9E75' : 'rgba(255,255,255,0.15)'}`,
                borderRadius: 16,
                padding: '40px 24px',
                textAlign: 'center',
                cursor: 'pointer',
                background: dragOver ? 'rgba(29,158,117,0.05)' : 'transparent',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 12 }}>📂</div>
              <div style={{ color: '#fff', fontWeight: 600, marginBottom: 6 }}>
                Glissez votre fichier CSV ici
              </div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
                ou cliquez pour sélectionner · Max 200 lignes · .csv uniquement
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            <div style={{ marginTop: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
              Colonnes requises : contact_name · contact_phone · dropoff_address ·
              size · weight · fragile · payment_method · price · notes
            </div>
          </div>
        )}

        {/* Step: preview */}
        {step === 'preview' && parseResult && (
          <div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              <StatBadge label="Total"   value={parseResult.totalCount}   color="#fff" />
              <StatBadge label="Valides" value={parseResult.validCount}   color="#1D9E75" />
              <StatBadge label="Erreurs" value={parseResult.invalidCount} color="#FF4D4F" />
            </div>

            <ImportCSVPreviewTable rows={parseResult.rows} />

            {parseResult.invalidCount > 0 && (
              <div style={{
                background: 'rgba(255,77,79,0.08)',
                border: '1px solid rgba(255,77,79,0.25)',
                borderRadius: 10, padding: '10px 14px',
                color: '#FF4D4F', fontSize: 13, marginTop: 12,
              }}>
                ⚠️ {parseResult.invalidCount} ligne(s) invalide(s) seront ignorées.
                Seules les {parseResult.validCount} lignes valides seront importées.
              </div>
            )}

            {parseResult.validCount === 0 && (
              <div style={{ color: '#FF4D4F', textAlign: 'center', marginTop: 12 }}>
                Aucune ligne valide à importer. Corrigez votre fichier et recommencez.
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button
                onClick={() => { setStep('upload'); setParseResult(null) }}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 10, color: '#fff',
                  fontSize: 14, fontWeight: 600,
                  padding: '10px 18px', cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                ← Changer de fichier
              </button>
              <button
                onClick={handleImport}
                disabled={parseResult.validCount === 0}
                style={{
                  background: parseResult.validCount === 0 ? 'rgba(29,158,117,0.3)' : '#1D9E75',
                  border: 'none', borderRadius: 10,
                  color: '#fff', fontSize: 14, fontWeight: 600,
                  padding: '10px 18px',
                  cursor: parseResult.validCount === 0 ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Importer {parseResult.validCount} colis
              </button>
            </div>
          </div>
        )}

        {/* Step: importing */}
        {step === 'importing' && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 32, marginBottom: 16 }}>⏳</div>
            <div style={{ color: '#fff', fontWeight: 600, marginBottom: 8 }}>
              Import en cours...
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 24 }}>
              {progress.current} / {progress.total} colis traités
            </div>
            <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 8, height: 8, overflow: 'hidden' }}>
              <div style={{
                width: `${percent}%`, height: '100%',
                background: '#1D9E75', borderRadius: 8,
                transition: 'width 0.3s ease',
              }} />
            </div>
            <div style={{ color: '#1D9E75', fontWeight: 700, marginTop: 8 }}>{percent}%</div>
            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, marginTop: 16 }}>
              Ne fermez pas cette fenêtre
            </div>
          </div>
        )}

        {/* Step: done */}
        {step === 'done' && (
          <div style={{ textAlign: 'center', padding: '32px 20px' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>
              {results.failed === 0 ? '✅' : '⚠️'}
            </div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 18, marginBottom: 16 }}>
              Import terminé
            </div>

            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginBottom: 24 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#1D9E75', fontSize: 28, fontWeight: 700 }}>{results.success}</div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>colis importés</div>
              </div>
              {results.failed > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#FF4D4F', fontSize: 28, fontWeight: 700 }}>{results.failed}</div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>échoués</div>
                </div>
              )}
            </div>

            {Object.keys(rowErrors).length > 0 && (
              <div style={{
                background: 'rgba(255,77,79,0.06)',
                border: '1px solid rgba(255,77,79,0.2)',
                borderRadius: 10, padding: 14,
                textAlign: 'left', marginBottom: 20,
                maxHeight: 140, overflowY: 'auto',
              }}>
                {Object.entries(rowErrors).map(([lineIndex, msg]) => (
                  <div key={lineIndex} style={{ color: '#FF4D4F', fontSize: 12, marginBottom: 4 }}>
                    Ligne {lineIndex} : {msg}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              {results.failed > 0 && (
                <button
                  onClick={() => { setStep('upload'); setParseResult(null) }}
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 10, color: '#fff',
                    fontSize: 14, fontWeight: 600,
                    padding: '10px 18px', cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Réessayer
                </button>
              )}
              <button
                onClick={() => { onSuccess?.(results.success); handleClose() }}
                style={{
                  background: '#1D9E75', border: 'none',
                  borderRadius: 10, color: '#fff',
                  fontSize: 14, fontWeight: 600,
                  padding: '10px 18px', cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
